import React, { useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";
import { ASSETS, LeafCorner } from "../components/Decorative";
import { IceCream, Clock } from "lucide-react";

const SpeisekartePage = () => {
    const [categories, setCategories] = useState([]);
    const [items, setItems] = useState([]);
    const [todays, setTodays] = useState(null);
    const [info, setInfo] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get("/menu/categories").then((r) => setCategories(r.data)),
            api.get("/menu/items").then((r) => setItems(r.data)),
            api.get("/todays-ice").then((r) => setTodays(r.data)),
            api.get("/site-info").then((r) => setInfo(r.data)),
        ]).catch(() => {});
    }, []);

    const itemsByCategory = (catId) => items.filter((i) => i.category_id === catId);
    const showTodays = info?.show_todays_ice !== false;

    return (
        <div data-testid="speisekarte-page">
            <section className="relative garden-bg py-20 md:py-24 overflow-hidden">
                <LeafCorner className="absolute top-0 left-0 w-60 h-60 opacity-80" />
                <LeafCorner className="absolute top-0 right-0 w-60 h-60 opacity-80" flip />
                <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative">
                    <img src={ASSETS.mascot} alt="" className="w-32 mx-auto mb-4 animate-float" aria-hidden="true" />
                    <span className="font-cursive text-3xl text-pastel-pink-dark">~ unser Angebot ~</span>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-deep-green mt-2">Unser Angebot</h1>
                    <p className="text-deep-green/70 mt-4 max-w-xl mx-auto">
                        Frisches Softeis, Becher, Snacks und Getränke. Einfach lecker.
                    </p>
                </div>
            </section>

            {/* HEUTIGES EIS (auch hier anzeigen) */}
            {showTodays && (
                <section className="py-14 md:py-16 cream-section" data-testid="todays-section-menu">
                    <div className="max-w-6xl mx-auto px-5 sm:px-8">
                        <div className="text-center mb-8">
                            <span className="font-cursive text-2xl text-pastel-pink-dark">~ heute frisch ~</span>
                            <h2 className="font-serif text-3xl sm:text-4xl text-deep-green mt-1">
                                {todays?.headline || "Heutiges Softeis"}
                            </h2>
                            {todays?.todays_hours && (
                                <div className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full bg-garden-green text-white shadow-md" data-testid="menu-todays-hours">
                                    <Clock className="w-4 h-4" />
                                    <span className="font-sans font-semibold text-sm">Heute geöffnet: {todays.todays_hours}</span>
                                </div>
                            )}
                        </div>
                        <div className="bg-white rounded-3xl shadow-[0_20px_50px_rgba(44,76,59,0.08)] border border-cream-dark p-6 md:p-10 relative overflow-hidden">
                            <div className="absolute -top-10 -right-10 w-40 h-40 bg-pastel-pink/30 rounded-full blur-2xl" />
                            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-leaf-green/30 rounded-full blur-2xl" />
                            <div className="relative grid md:grid-cols-3 gap-6 items-center">
                                <div className="md:col-span-1 grid place-items-center">
                                    <div className="banner-ribbon font-serif text-lg">
                                        {todays?.date_label || "Heute"}
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    {todays && todays.flavors?.length > 0 ? (
                                        <ul className="grid sm:grid-cols-2 gap-3">
                                            {todays.flavors.map((flavor, i) => (
                                                <li
                                                    key={i}
                                                    className="group flex items-center gap-3 p-3 rounded-2xl bg-cream-dark/60"
                                                    data-testid={`menu-today-flavor-${i}`}
                                                >
                                                    <IceCream className="w-5 h-5 text-garden-green-dark flex-shrink-0" />
                                                    <span className="font-sans font-medium text-base text-deep-green">{flavor}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-deep-green/70">Heute keine besonderen Sorten eingetragen.</p>
                                    )}
                                    {todays?.note && (
                                        <p className="mt-4 font-cursive text-xl text-garden-green-dark">{todays.note}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <section className="py-16 md:py-20" data-testid="menu-list">
                <div className="max-w-5xl mx-auto px-5 sm:px-8">
                    {categories.length === 0 ? (
                        <div className="text-center py-20 text-deep-green/60">
                            <IceCream className="w-12 h-12 mx-auto mb-4 text-leaf-green" />
                            Noch keine Speisekarte verfügbar.
                        </div>
                    ) : (
                        categories.map((cat) => (
                            <div key={cat.id} className="mb-16" data-testid={`menu-category-${cat.id}`}>
                                <div className="flex items-end justify-between border-b border-leaf-green/40 pb-3 mb-6">
                                    <div>
                                        <h2 className="font-serif text-3xl text-deep-green">{cat.name}</h2>
                                        {cat.description && (
                                            <p className="text-deep-green/70 text-sm mt-1">{cat.description}</p>
                                        )}
                                    </div>
                                    <span className={`font-cursive text-2xl text-pastel-pink-dark hidden sm:inline-block ${cat.name.trim().toLowerCase() === "getränke" ? "sm:hidden" : ""}`}>~ frisch zubereitet ~</span>
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    {itemsByCategory(cat.id).length === 0 ? (
                                        <p className="text-deep-green/50 text-sm italic col-span-2">Keine Artikel in dieser Kategorie.</p>
                                    ) : (
                                        itemsByCategory(cat.id).map((item) => (
                                            <div
                                                key={item.id}
                                                className="bg-white rounded-2xl p-5 border border-cream-dark hover-lift flex gap-4"
                                                data-testid={`menu-item-${item.id}`}
                                            >
                                                {item.image_url && (
                                                    <img
                                                        src={fileUrl(item.image_url)}
                                                        alt={item.name}
                                                        className="w-20 h-20 rounded-xl object-cover flex-shrink-0 border border-cream-dark"
                                                        loading="lazy"
                                                    />
                                                )}
                                                <div className="flex-1 flex justify-between gap-3 min-w-0">
                                                    <div className="flex-1 min-w-0">
                                                        <div className="font-serif text-lg text-deep-green leading-snug">{item.name}</div>
                                                        {item.description && (
                                                            <div className="text-sm text-deep-green/65 mt-1 leading-relaxed">{item.description}</div>
                                                        )}
                                                    </div>
                                                    <div className="price-tag whitespace-nowrap self-start" data-testid={`menu-item-price-${item.id}`}>
                                                        {item.price}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </section>
        </div>
    );
};

export default SpeisekartePage;
